import { v4 as uuidv4 } from "uuid";
import { ActorState } from "../../../models/ActorState";
import Interaction, {
  InteractionType,
  Marker,
} from "../../../models/interactions/Interaction";
import { InteractionProperty } from "../../../models/InteractionProperty";
import Take360, { CreateTake360Copy } from "../../../models/Take360";
import {
  TrainingState,
  selectInteractionIndexById,
  selectSelectedTake,
  selectTakeIndexById,
} from "../TrainingSlice";
import {
  findQuizFeedbackInteraction,
  getNextSelectedAfterFeedbackRemoval,
  insertQuizFeedbackInteraction,
  removeQuizFeedbackInteraction,
  reorderQuizFeedbackInteractions,
  replaceQuizFeedbackInteraction,
} from "../helpers/QuizFeedbackLocator";
import { UpdatePropertyOfInteraction } from "../helpers/TrainingSliceHelper";
import {
  areMoveSourcesFeedbackEligible,
  moveInteraction,
  moveInteractionsBatch,
} from "../helpers/InteractionMove";
import { FEEDBACK_ELIGIBLE_TYPES_360 } from "../../../models/QuizFeedbackHelper";
import { moveSequenceSelection } from "../helpers/SequenceMove";
import { TrainingSliceStrategy } from "./TrainingSliceStrategy";

export const TrainingSliceStrategy360: TrainingSliceStrategy = {
  setSelectedTake(state, takeId) {
    if (!state.takes360.find((take) => take.id === takeId)) return {};

    return {
      selectedTake: takeId,
      selectedElementId: takeId,
      selectedElementIsInteraction: false,
      selectedElementIsTake: true,
    };
  },
  addNewTake(state, name, assetId) {
    const newTake: Take360 = {
      assetId: assetId!,
      name: name!,
      id: uuidv4(),
      interactions: [],
      rotation: 0,
      display360: false,
      originalRotation: {
        id: uuidv4(),
        name: "Teleport Marker",
        type: InteractionType.Marker,
        xPos: 0.5,
        yPos: 0.5,
      },
      teleportMarker: {
        id: uuidv4(),
        name: "Teleport Marker",
        type: InteractionType.TeleportMarker,
        xPos: 0.5,
        yPos: 0.5,
      },
      automaticTransition: false,
      is360Image: true,
      isExpanded: true,
    };
    const copy = [...state.takes360];
    copy.push(newTake);
    state.selectedTake = newTake.id; // automatically select new take
    state.selectedElementId = newTake.id;
    state.isDirty = true;

    return {
      selectedTake: newTake.id,
      selectedElementId: newTake.id,
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      takes360: copy,
    };
  },
  addTakeCopy(state, take, _transition, _interactions, _sequence) {
    const originalTake = take as Take360;

    const newTake = CreateTake360Copy(
      originalTake.name,
      originalTake.assetId,
      originalTake.rotation,
      { ...originalTake.originalRotation },
      { ...originalTake.teleportMarker },
      originalTake.automaticTransition,
      originalTake.is360Image,
      originalTake.display360
    );
    const copy = [...state.takes360];

    const indexSelectedTake: number = state.selectedElementIsTake
      ? copy.findIndex((t) => t.id === state.selectedElementId)
      : -1;

    if (indexSelectedTake !== -1) {
      copy.splice(indexSelectedTake + 1, 0, newTake);
    } else {
      copy.push(newTake);
    }

    return {
      selectedTake: newTake.id,
      selectedElementId: newTake.id,
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      isDirty: true,
      takes360: copy,
    };
  },
  setSelectedInteraction(state, interactionId) {
    const parent = state.takes360.find(
      (take) =>
        take.interactions.findIndex(
          (interaction) => interaction.id === interactionId
        ) !== -1 || take.teleportMarker.id === interactionId
    );

    if (parent) {
      return {
        selectedTake: parent.id,
        selectedElementId: interactionId,
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
      };
    }

    const feedbackLocation = findQuizFeedbackInteraction(
      state.takes360,
      interactionId
    );
    if (feedbackLocation) {
      return {
        selectedTake: feedbackLocation.takeId,
        selectedElementId: interactionId,
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
      };
    }

    return {};
  },
  addInteractionToTake(state, interaction: Interaction, takeId) {
    const copiedTakes = [...state.takes360];
    const index = copiedTakes.findIndex((take) => take.id === takeId);
    const takeCopy = copiedTakes[index];
    if (!takeCopy) return {};

    const copiedInteractions = [...takeCopy.interactions];

    copiedInteractions.push(interaction);
    copiedTakes[index] = {
      ...copiedTakes[index],
      interactions: copiedInteractions,
    };

    return {
      takes360: copiedTakes,
      selectedTake: takeId,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      isDirty: true,
    };
  },
  addInteractionToSelectedTake(state, interaction) {
    //const take: Take360 =
    //  state.takes360[selectTakeIndexById(state.takes360, state.selectedTake)];
    //take.interactions.push(interaction);
    // state.selectedElementId = interaction.id;
    // state.selectedElementIsTake = false;
    // state.selectedElementIsInteraction = true;
    // state.isDirty = true;

    const takeIndex = selectTakeIndexById(state.takes360, state.selectedTake);
    if (takeIndex === -1) return {};

    const takeCopy = { ...state.takes360[takeIndex] };
    takeCopy.interactions.push(interaction);
    const takesCopy = [...state.takes360];
    takesCopy[takeIndex] = takeCopy;

    return {
      takes360: takesCopy,
      selectedTake: state.selectedTake,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      isDirty: true,
    };
  },
  addInteractionCopy(state, interaction) {
    const takesCopy = [...state.takes360];
    const index = selectTakeIndexById(takesCopy, state.selectedTake);
    if (index === -1) return {};

    if (state.selectedElementIsInteraction) {
      const feedbackLocation = findQuizFeedbackInteraction(
        state.takes360,
        state.selectedElementId
      );
      if (feedbackLocation) {
        if (!FEEDBACK_ELIGIBLE_TYPES_360.has(interaction.type)) return {};
        const newTakes = insertQuizFeedbackInteraction(
          state.takes360,
          feedbackLocation.quizId,
          feedbackLocation.slot,
          interaction,
          feedbackLocation.feedbackIndex + 1
        );
        if (!newTakes) return {};
        return {
          takes360: newTakes,
          selectedTake: feedbackLocation.takeId,
          selectedElementId: interaction.id,
          selectedElementIsTake: false,
          selectedElementIsInteraction: true,
          isDirty: true,
        };
      }

      const interactionIndex = selectInteractionIndexById(
        takesCopy[index],
        state.selectedElementId
      );
      takesCopy[index].interactions.splice(
        interactionIndex + 1,
        0,
        interaction
      );
    } else {
      takesCopy[index].interactions.unshift(interaction);
    }

    return {
      takes360: takesCopy,
      selectedTake: state.selectedTake,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      isDirty: true,
    };
  },
  addInteractionToQuizFeedback(state, quizId, slot, interaction) {
    if (!FEEDBACK_ELIGIBLE_TYPES_360.has(interaction.type)) return {};
    const newTakes = insertQuizFeedbackInteraction(
      state.takes360,
      quizId,
      slot,
      interaction
    );
    if (!newTakes) return {};

    const parentTake = newTakes.find((take) =>
      take.interactions.some((i) => i.id === quizId)
    );

    return {
      takes360: newTakes,
      selectedTake: parentTake?.id ?? state.selectedTake,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      isDirty: true,
    };
  },
  reorderQuizFeedbackInteractions(state, quizId, slot, startIndex, endIndex) {
    const newTakes = reorderQuizFeedbackInteractions(
      state.takes360,
      quizId,
      slot,
      startIndex,
      endIndex
    );
    if (!newTakes) return {};

    return {
      takes360: newTakes,
      isDirty: true,
    };
  },
  deleteInteraction(state, interaction) {
    const takesCopy = [...state.takes360];
    const parentIndex = takesCopy.findIndex((take) =>
      take.interactions.find((i) => i.id === interaction.id)
    );
    if (parentIndex === -1) {
      const feedbackLocation = findQuizFeedbackInteraction(
        state.takes360,
        interaction.id
      );
      if (!feedbackLocation) return {};

      const newTakes = removeQuizFeedbackInteraction(
        state.takes360,
        feedbackLocation
      );
      return {
        takes360: newTakes,
        selectedTake: feedbackLocation.takeId,
        selectedElementId: getNextSelectedAfterFeedbackRemoval(
          newTakes,
          feedbackLocation
        ),
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
        isDirty: true,
      };
    }

    const parentTake = takesCopy[parentIndex];
    const deletedInteractionIndex = parentTake.interactions.findIndex(
      (i) => i.id === interaction.id
    );
    if (deletedInteractionIndex === -1) return {};

    parentTake.interactions.splice(deletedInteractionIndex, 1);

    const nextInteraction = parentTake.interactions[deletedInteractionIndex];
    const previousInteraction =
      parentTake.interactions[deletedInteractionIndex - 1];
    const nextSelectedElement =
      nextInteraction?.id ?? previousInteraction?.id ?? parentTake.id;
    const isInteractionSelected = nextSelectedElement !== parentTake.id;

    return {
      takes360: takesCopy,
      selectedTake: parentTake.id,
      selectedElementId: nextSelectedElement,
      selectedElementIsTake: !isInteractionSelected,
      selectedElementIsInteraction: isInteractionSelected,
      isDirty: true,
    };
  },
  updateSelectedTake(state, take) {
    const ti = selectTakeIndexById(state.takes360, take.id);
    const copy = [...state.takes360];
    copy[ti] = { ...(take as Take360) };

    return {
      takes360: copy,
      isDirty: true,
    };
  },
  setTakes(state, takes) {
    return {
      takes360: takes as Take360[],
      selectedTake: takes.length > 0 ? (takes[0] as Take360).id : "",
      selectedElementId: takes.length > 0 ? (takes[0] as Take360).id : "",
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      isDirty: false,
    };
  },
  selectSelectedElement(state) {
    // take 360
    const take = selectSelectedTake(state);

    if (state.training.selectedElementIsTake) {
      return take;
    } else {
      if (take?.teleportMarker.id === state.training.selectedElementId)
        return take.teleportMarker;

      const interaction = take?.interactions.find(
        (interaction) => interaction.id === state.training.selectedElementId
      );

      return (
        interaction ??
        findQuizFeedbackInteraction(
          state.training.takes360,
          state.training.selectedElementId
        )?.interaction
      );
    }
  },
  updateSelectedInteraction(state, interaction) {
    const copiedTakes = [...state.takes360];
    const takeIndex = selectTakeIndexById(copiedTakes, state.selectedTake);
    if (copiedTakes[takeIndex].teleportMarker.id === interaction.id) {
      copiedTakes[takeIndex].teleportMarker = { ...(interaction as Marker) };
    } else if (copiedTakes[takeIndex].originalRotation.id === interaction.id) {
      copiedTakes[takeIndex].originalRotation = { ...(interaction as Marker) };
    } else {
      const interactionIndex = selectInteractionIndexById(
        copiedTakes[takeIndex],
        interaction.id
      );
      if (interactionIndex === -1) {
        const feedbackLocation = findQuizFeedbackInteraction(
          state.takes360,
          interaction.id
        );
        if (!feedbackLocation) return {};
        return {
          takes360: replaceQuizFeedbackInteraction(
            state.takes360,
            feedbackLocation,
            interaction
          ),
          isDirty: true,
        };
      }
      copiedTakes[takeIndex].interactions[interactionIndex] = {
        ...interaction,
      };
    }
    return {
      takes360: copiedTakes,
      isDirty: true,
    };
  },
  updateSelectedInteractionProperty: function (
    state: TrainingState,
    interactionProperty: InteractionProperty
  ) {
    const copiedTakes = [...state.takes360];
    const takeIndex = selectTakeIndexById(copiedTakes, state.selectedTake);
    const interactionIndex = selectInteractionIndexById(
      copiedTakes[takeIndex],
      state.selectedElementId
    );
    if (interactionIndex === -1) {
      const feedbackLocation = findQuizFeedbackInteraction(
        state.takes360,
        state.selectedElementId
      );
      if (!feedbackLocation) return {};
      return {
        takes360: replaceQuizFeedbackInteraction(
          state.takes360,
          feedbackLocation,
          UpdatePropertyOfInteraction(
            feedbackLocation.interaction,
            interactionProperty
          )
        ),
        isDirty: true,
      };
    }
    const properties =
      copiedTakes[takeIndex].interactions[interactionIndex].properties!;
    const pi = properties.findIndex((p) => p.name === interactionProperty.name);
    properties[pi] = interactionProperty;

    return {
      takes360: copiedTakes,
      isDirty: true,
    };
  },
  deleteTake: (state, takeId) => {
    let index = state.takes360.findIndex((take) => take.id === takeId) - 1;
    index = index >= 0 ? index : 0;

    const copied = [...state.takes360];
    const newTakes = copied.filter((take) => take.id !== takeId);

    return {
      takes360: newTakes,
      selectedTake: newTakes.length > 0 ? newTakes[index].id : "",
      selectedElementId: newTakes.length > 0 ? newTakes[index].id : "",
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      isDirty: true,
    };
  },
  reorderTakes: (state, startIndex, endIndex) => {
    const copied = [...state.takes360];
    if (
      startIndex < 0 ||
      endIndex < 0 ||
      startIndex >= copied.length ||
      endIndex >= copied.length ||
      startIndex === endIndex
    ) {
      return {};
    }
    const [removed] = copied.splice(startIndex, 1);
    if (!removed) return {};
    copied.splice(endIndex, 0, removed);
    return {
      takes360: copied,
      isDirty: true,
    };
  },
  reorderInteractions: (
    state,
    startTakeId,
    destinationTakeId,
    startIndex,
    endIndex
  ) => {
    const copied = [...state.takes360];
    const startTakeIndex = selectTakeIndexById(state.takes360, startTakeId);
    const destinationTakeIndex = selectTakeIndexById(
      state.takes360,
      destinationTakeId
    );
    if (startTakeIndex === -1 || destinationTakeIndex === -1) return {};
    const startTake = copied[startTakeIndex];
    const destTake = copied[destinationTakeIndex];
    if (startIndex < 0 || startIndex >= startTake.interactions.length) {
      return {};
    }
    if (endIndex < 0) return {};
    const [removed] = startTake.interactions.splice(startIndex, 1);
    if (!removed) return {};
    const normalizedEndIndex = Math.min(endIndex, destTake.interactions.length);
    destTake.interactions.splice(normalizedEndIndex, 0, removed);

    return {
      takes360: copied,
      selectedTake: destTake.id,
      isDirty: true,
    };
  },
  moveInteraction(state, payload) {
    if (
      payload.destination.kind === "quizFeedback" &&
      !areMoveSourcesFeedbackEligible(
        state.takes360,
        [payload.interactionId],
        FEEDBACK_ELIGIBLE_TYPES_360
      )
    ) {
      return {};
    }
    const result = moveInteraction(state.takes360, payload);
    if (!result) return {};

    return {
      takes360: result.takes,
      selectedTake: result.destinationTakeId,
      selectedElementId: result.interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      isDirty: true,
    };
  },
  moveInteractions(state, payload) {
    if (
      payload.destination.kind === "quizFeedback" &&
      !areMoveSourcesFeedbackEligible(
        state.takes360,
        payload.interactionIds,
        FEEDBACK_ELIGIBLE_TYPES_360
      )
    ) {
      return {};
    }
    const result = moveInteractionsBatch(state.takes360, payload);
    if (!result) return {};

    const movedIds = result.interactions.map((interaction) => interaction.id);
    const primary = movedIds.includes(state.selectedElementId)
      ? state.selectedElementId
      : movedIds[0];
    return {
      takes360: result.takes,
      selectedTake: result.destinationTakeId,
      selectedElementId: primary,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      selectedInteractionIds: movedIds,
      selectionAnchorId: movedIds.includes(state.selectionAnchorId)
        ? state.selectionAnchorId
        : primary,
      isDirty: true,
    };
  },
  moveSequenceSelection(state, payload) {
    const result = moveSequenceSelection(state.takes360, payload);
    if (!result.ok) return {};
    return {
      takes360: result.takes as Take360[],
      selectedTake: payload.destination.takeId,
      selectedElementId: payload.destination.sequenceId,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      isDirty: true,
    };
  },
  updateSelectedTakeProperty: function (
    state: TrainingState,
    takeProperty: InteractionProperty
  ) {
    throw new Error("Function not implemented.");
  },
  selectSelectedElementType: (state: TrainingState) => {
    // take
    const selectedTake = state.takes360.find(
      (take) => take.id === state.selectedElementId
    );
    if (selectedTake) return "take";

    // interaction
    const selectedInteraction = state.takes360.find((take) =>
      take.interactions.find(
        (interaction) => interaction.id === state.selectedElementId
      )
    );
    if (selectedInteraction) return "interaction";

    // nested quiz feedback interaction
    if (findQuizFeedbackInteraction(state.takes360, state.selectedElementId))
      return "interaction";

    return undefined;
  },
  updateSelectedActorState: function (
    state: TrainingState,
    actorState: ActorState
  ) {
    throw new Error("Function not implemented.");
  },
  addPropToSelectedTake(
    state: TrainingState,
    propId: string,
    locationId: string
  ) {
    throw new Error("Function not implemented.");
  },
  removePropFromSelectedTake(state: TrainingState, id: string) {
    throw new Error("Function not implemented.");
  },
  addSituationToTakeLocation(
    state: TrainingState,
    situationId: string,
    takeLocationId: string
  ) {
    throw new Error("Function not implemented.");
  },
  removeSituationFromTakeLocation(state, takeLocationId) {
    throw new Error("Function not implemented.");
  },
  addAttachmentToTake(state, actorId, propId, tag, playerToProp) {
      throw new Error("Function not implemented.");
    },
  removeAttachmentFromTake(state, actorId, propId) {
      throw new Error("Function not implemented.");
    },
  };

